import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, CheckCircleOutlined, InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { ordersService } from '../../services';
import { ProductCategory } from '../../constants/productCategory';
import { useAuth } from '../../contexts/AuthContext';
import { useProducts } from '../../contexts/ProductsContext';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { useMarketplacePricing } from '../../hooks/useMarketplacePricing';
import MarketplacePricingSelector from '../../components/MarketplacePricingSelector';

// ─── Types ───────────────────────────────────────────────
interface GhostConfig {
  name: string;
  projectName: string;
  region: string;
  adminEmail: string;
}

// ─── Component ───────────────────────────────────────────
const CreateGhost: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { products: allProducts, getProductsByCategory } = useProducts();
  const { success, error: showError } = useNotificationContext();

  const [step, setStep] = useState(1);
  const pricing = useMarketplacePricing();
  const [isCreating, setIsCreating] = useState(false);

  const [config, setConfig] = useState<GhostConfig>({
    name: '',
    projectName: '',
    region: 'us-east-1',
    adminEmail: '',
  });

  const products = allProducts[ProductCategory.GHOST] || [];

  useEffect(() => {
    if (accessToken && products.length === 0) {
      getProductsByCategory(ProductCategory.GHOST);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // ─── Validations ───────────────────────────────────────
  const isStepValid = (): boolean => {
    switch (step) {
      case 1: return !!(config.name.trim() && pricing.isReady);
      case 2: return config.adminEmail.includes('@') && config.adminEmail.includes('.');
      case 3: return true;
      default: return false;
    }
  };

  // ─── Handlers ──────────────────────────────────────────
  const handleNext = () => {
    if (step < 3 && isStepValid()) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleCreate = async () => {
    if (!config.name || !pricing.pricingSelection) {
      showError(t('ghost.validation.missingFields') || 'Please complete all required fields.');
      return;
    }

    setIsCreating(true);
    try {
      const selectedProduct = products.find((p: any) => p.category === 'GHOST') || products[0];

      if (!selectedProduct || !selectedProduct.id) {
        showError(t('ghost.validation.noProduct') || 'No product available. Please try again.');
        setIsCreating(false);
        return;
      }

      const instanceType = pricing.selectedVariant?.providerConfiguration?.instanceType ?? 't3.medium';
      const storage = pricing.selectedVariant?.includedResources?.storageGb ?? 30;

      const orderData: any = {
        productId: selectedProduct.id,
        quantity: 1,
        metadata: {
          type: 'GHOST',
          ghostConfig: {
            pricingSelection: pricing.pricingSelection,
            name: config.name.trim(),
            instanceType,
            storage,
            projectName: (config.projectName.trim() || config.name.trim()),
            region: config.region,
            adminEmail: config.adminEmail,
          },
        },
      };

      const createdOrder = await ordersService.create(orderData);
      success(t('ghost.create.success') || 'Ghost CMS order created successfully');
      navigate(`/payment-selection/${createdOrder.id}`);
    } catch (error: any) {
      console.error('Error creating Ghost order:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error creating order.';
      showError(errorMessage);
      setIsCreating(false);
    }
  };

  // ─── Step 1: Infrastructure ────────────────────────────
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          {t('ghost.step1.title') || 'Infrastructure Configuration'}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('ghost.step1.subtitle') || 'Configure the server that will host your Ghost CMS instance.'}
        </p>
      </div>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('ghost.fields.name') || 'Service Name'} *
        </label>
        <input
          type="text"
          value={config.name}
          onChange={(e) => setConfig({ ...config, name: e.target.value })}
          placeholder="my-ghost-blog"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('ghost.fields.nameHint') || 'A unique name to identify your Ghost CMS service.'}
        </p>
      </div>

      {/* Pricing plans */}
      <MarketplacePricingSelector
        basePlans={pricing.basePlans}
        variantsForSelected={pricing.variantsForSelected}
        selectedPlanCode={pricing.selectedPlanCode}
        selectedVariantCode={pricing.selectedVariantCode}
        addons={pricing.addons}
        selectedAddonCodes={pricing.selectedAddonCodes}
        isLoadingPlans={pricing.isLoadingPlans}
        isLoadingAddons={pricing.isLoadingAddons}
        onSelectPlan={pricing.setSelectedPlanCode}
        onSelectVariant={pricing.setSelectedVariantCode}
        onToggleAddon={pricing.toggleAddon}
      />

      {/* Region (fixed) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('ghost.fields.region') || 'Region'}
        </label>
        <div className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300">
          US East (N. Virginia)
        </div>
      </div>
    </div>
  );

  // ─── Step 2: Configuration ─────────────────────────────
  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          {t('ghost.step2.title') || 'Ghost Configuration'}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('ghost.step2.subtitle') || 'Configure settings for your Ghost CMS instance.'}
        </p>
      </div>

      {/* Admin Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('ghost.fields.adminEmail') || 'Admin Email'} *
        </label>
        <input
          type="email"
          value={config.adminEmail}
          onChange={(e) => setConfig({ ...config, adminEmail: e.target.value })}
          placeholder="admin@kubiy.com"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('ghost.fields.adminEmailHint') || 'Email address for the admin account that will be created automatically.'}
        </p>
      </div>

      {/* Admin Password Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {t('ghost.step2.adminPassword') || 'Admin password'}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              {t('ghost.step2.adminPasswordDesc') || 'A secure password will be auto-generated and stored in Secrets Manager. You can view it in the service detail after deployment.'}
            </p>
          </div>
        </div>
      </div>

      {/* Project Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('ghost.fields.projectName') || 'Project Name'} <span className="text-gray-400">(optional)</span>
        </label>
        <input
          type="text"
          value={config.projectName}
          onChange={(e) => setConfig({ ...config, projectName: e.target.value })}
          placeholder={config.name || 'my-ghost-blog'}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('ghost.fields.projectNameHint') || 'Defaults to the service name if not provided.'}
        </p>
      </div>

      {/* Database info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {t('ghost.step2.database') || 'Database'}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              {t('ghost.step2.databaseDesc') || 'MySQL 8 is installed locally on the same instance. DB credentials are stored in Secrets Manager.'}
            </p>
          </div>
        </div>
      </div>

      {/* Ports info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {t('ghost.step2.portsInfo') || 'Ports that will be opened:'}
            </p>
            <ul className="text-sm text-blue-700 dark:text-blue-300 mt-1 space-y-0.5 list-disc list-inside">
              <li>2368 — Ghost (HTTP)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Features info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {t('ghost.step2.featuresTitle') || 'Included features:'}
            </p>
            <ul className="text-sm text-blue-700 dark:text-blue-300 mt-1 space-y-0.5 list-disc list-inside">
              <li>Blog & Pages</li>
              <li>Email Newsletters</li>
              <li>Member Subscriptions</li>
              <li>Built-in Themes</li>
              <li>SEO optimized</li>
              <li>Stripe payments</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Step 3: Review ────────────────────────────────────
  const renderStep3 = () => {
    const variant = pricing.selectedVariant;
    const inst = variant?.providerConfiguration?.instanceType ?? '—';
    const storageGb = variant?.includedResources?.storageGb;
    const totalStr = pricing.totalPrice
      ? `$${pricing.totalPrice.toLocaleString('es-MX', { minimumFractionDigits: 0 })} MXN`
      : '—';

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            {t('ghost.step3.title') || 'Review & Create'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('ghost.step3.subtitle') || 'Review your Ghost CMS configuration before creating the service.'}
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">
            {t('ghost.review.infrastructure') || 'Infrastructure'}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('ghost.fields.name') || 'Name'}</span>
              <span className="font-medium text-gray-900 dark:text-white">{config.name}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">Plan</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {variant?.displayName ?? variant?.planName ?? '—'} ({variant?.variantName})
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('ghost.fields.instanceType') || 'Instance'}</span>
              <span className="font-medium text-gray-900 dark:text-white">{inst}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('ghost.fields.storage') || 'Storage'}</span>
              <span className="font-medium text-gray-900 dark:text-white">{storageGb ? `${storageGb} GB (gp3)` : '—'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('ghost.fields.region') || 'Region'}</span>
              <span className="font-medium text-gray-900 dark:text-white">US East (N. Virginia)</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">
            {t('ghost.review.configuration') || 'Configuration'}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">Stack</span>
              <span className="font-medium text-gray-900 dark:text-white">Node.js 20 + MySQL 8</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">Admin Panel</span>
              <span className="font-medium text-gray-900 dark:text-white">/ghost/</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('ghost.review.adminEmail') || 'Admin Email'}</span>
              <span className="font-medium text-gray-900 dark:text-white">{config.adminEmail}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('ghost.review.adminPassword') || 'Password'}</span>
              <span className="font-medium text-gray-900 dark:text-white">{t('ghost.review.adminPasswordValue') || 'Auto-generated (Secrets Manager)'}</span>
            </div>
          </div>
        </div>

        {/* Cost */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-900 dark:text-white">
              Costo mensual
            </h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {totalStr}<span className="text-sm font-normal text-gray-500 dark:text-gray-400">/mes</span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  // ─── Render Step ───────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      default: return null;
    }
  };

  // ─── Main Render ───────────────────────────────────────
  return (
    <div className="max-w-[900px] mx-auto px-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/marketplace?product=ghost')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftOutlined />
          <span className="text-sm font-medium">{t('ghost.back') || 'Back to Ghost Services'}</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('ghost.create.title') || 'Create Ghost CMS Service'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('ghost.create.subtitle') || 'Deploy a managed Ghost publishing platform on dedicated infrastructure.'}
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[
            { num: 1, label: t('ghost.steps.infrastructure') || 'Infrastructure' },
            { num: 2, label: t('ghost.steps.ghostConfig') || 'Ghost Config' },
            { num: 3, label: t('ghost.steps.review') || 'Review' },
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${step >= s.num
                      ? 'bg-black dark:bg-white text-white dark:text-black'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                >
                  {step > s.num ? <CheckCircleOutlined /> : s.num}
                </div>
                <span
                  className={`text-xs mt-2 font-medium ${step >= s.num ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                    }`}
                >
                  {s.label}
                </span>
              </div>
              {idx < 2 && (
                <div
                  className={`h-1 flex-1 mx-2 rounded transition-all ${step > s.num ? 'bg-black dark:bg-white' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {renderStep()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('ghost.actions.back') || 'Back'}
        </button>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/marketplace?product=ghost')}
            className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('ghost.actions.cancel') || 'Cancel'}
          </button>
          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('ghost.actions.next') || 'Next'}
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <LoadingOutlined className="animate-spin" />
                  {t('ghost.actions.creating') || 'Creating order...'}
                </>
              ) : (
                t('ghost.actions.create') || 'Create Order'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateGhost;
